// @flow

import * as React from 'react';
import withFeatureFlags from '@core-ui/feature-flags';

const OPTIMIZELY_POLLING_FEATURE: string = 'react-personalization-polling';
const noOp = () => { };

const loadScript = (src: string, done: (error: Error | void) => void) => {
  const js = document.createElement('script');
  js.src = src;
  js.defer = true;
  js.onload = (): void => done && done();
  js.onerror = (): void => done && done(new Error(`Failed to load script ${src}`));

  if (document.head) {
    document.head.appendChild(js);
  }
};

const optimizelyUrl = ({ brand, type, market }): string =>
  `https://cdn.optimizely.com/public/6529002483/s/${brand}_${market}_${type}.js`;

const FALLBACK_PERSONALIZED_DATA = { components: {} };

type Props = {
  datalayer: DataLayer,
  abbrBrand: string,
  pageType: string,
  market: string,
  enabledFeatures: EnabledFeatures,
  highPriorityEvents: Array<any>,
  criticalResources: CriticalResources,
  children: React.Element<string>,
};

type State = {
  personalizedMarketingData: any,
};

class PageWith3rdParties extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.windowOnLoad = null;
  }

  state: State = { personalizedMarketingData: {} };

  componentDidMount() {
    const {
      abbrBrand,
      pageType,
      market,
      enabledFeatures = {},
      highPriorityEvents = [],
    } = this.props;

    this.windowOnLoad = () => {
      const load3rdParties = () => {
        loadScript(
          optimizelyUrl({
            brand: abbrBrand,
            type: pageType,
            market,
          }),
          enabledFeatures[OPTIMIZELY_POLLING_FEATURE]
            ? noOp
            : () => {
              this.setState((): State => ({
                personalizedMarketingData:
                  window.personalizedMarketingData ||
                  FALLBACK_PERSONALIZED_DATA,
              }));
            }
        );
      };
      if (highPriorityEvents.length) {
        Promise.all(
          highPriorityEvents.map(({ deferred }): Promise<any> => deferred.promise)
        ).then(() => {
          load3rdParties();
        });
      } else {
        load3rdParties();
      }
    };
    if (document.readyState === 'complete') {
      this.windowOnLoad();
    } else {
      window.addEventListener('load', this.windowOnLoad);
    }
  }
  componentWillUnmount() {
    window.removeEventListener('load', this.windowOnLoad);
  }

  windowOnLoad: null | void => void;

  render(): React.Node {
    const { criticalResources, abbrBrand, market, pageType } = this.props;

    criticalResources.push({
      url: optimizelyUrl({
        brand: abbrBrand,
        type: pageType,
        market,
      }),
      type: 'script',
    });
    return React.cloneElement(this.props.children, {
      personalizedMarketingData: this.state.personalizedMarketingData,
    });
  }
}

export { PageWith3rdParties };
export default withFeatureFlags(PageWith3rdParties);
